import { useState, useEffect } from "react";
import { useResources } from "@/hooks/rbac";
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from "@/components/ui/table";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Loader2, Shield, Eye, Edit, X } from "lucide-react";
import { PermissionLevel } from "@/types/rbac";

interface RolePermissionSelectorProps {
  permissions: Record<string, PermissionLevel>;
  onPermissionsChange: (permissions: Record<string, PermissionLevel>) => void;
}

export default function RolePermissionSelector({ 
  permissions, 
  onPermissionsChange 
}: RolePermissionSelectorProps) {
  const { data: resources, isLoading: resourcesLoading } = useResources();
  
  const handlePermissionChange = (resourceId: string, value: PermissionLevel) => {
    onPermissionsChange({
      ...permissions,
      [resourceId]: value
    });
  };

  const getPermissionIcon = (level: PermissionLevel) => {
    switch (level) {
      case 'none': return <X className="h-4 w-4 text-muted-foreground" />;
      case 'view': return <Eye className="h-4 w-4 text-info" />;
      case 'edit': return <Edit className="h-4 w-4 text-success" />;
    }
  };

  const getPermissionCount = () => {
    const totalResources = resources?.length || 0;
    const configuredCount = Object.values(permissions).filter(p => p !== 'none').length;
    return { configuredCount, totalResources };
  };

  const setAllPermissions = (level: PermissionLevel) => {
    if (!resources) return;
    
    const newPermissions: Record<string, PermissionLevel> = {};
    resources.forEach(resource => {
      newPermissions[resource.id] = level;
    });
    onPermissionsChange(newPermissions);
  };

  const clearAllPermissions = () => {
    if (!resources) return;
    
    const newPermissions: Record<string, PermissionLevel> = {};
    resources.forEach(resource => {
      newPermissions[resource.id] = 'none';
    });
    onPermissionsChange(newPermissions);
  };

  if (resourcesLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  const { configuredCount, totalResources } = getPermissionCount();

  return (
    <div className="space-y-4">
      <GlassCard variant="default" hover={false}>
        <GlassCardHeader className="pb-3">
          <GlassCardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Resource Permissions
          </GlassCardTitle>
          <GlassCardDescription>
            Configure what this role can access. You have configured {configuredCount} of {totalResources} resources.
          </GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent className="space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAllPermissions('view')}
            >
              <Eye className="h-4 w-4 mr-1" />
              All View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAllPermissions('edit')}
            >
              <Edit className="h-4 w-4 mr-1" />
              All Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllPermissions}
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          </div>

          {/* Permissions Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Resource</TableHead>
                  <TableHead>Permission Level</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resources?.map((resource) => (
                  <TableRow key={resource.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {getPermissionIcon(permissions[resource.id] || 'none')}
                        {resource.name}
                      </div>
                      {resource.description && (
                        <div className="text-sm text-muted-foreground mt-1">
                          {resource.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <RadioGroup
                        value={permissions[resource.id] || 'none'}
                        onValueChange={(value) => 
                          handlePermissionChange(resource.id, value as PermissionLevel)
                        }
                        className="flex space-x-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="none" id={`${resource.id}-none`} />
                          <Label htmlFor={`${resource.id}-none`} className="text-sm">
                            None
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="view" id={`${resource.id}-view`} />
                          <Label htmlFor={`${resource.id}-view`} className="text-sm">
                            View
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="edit" id={`${resource.id}-edit`} />
                          <Label htmlFor={`${resource.id}-edit`} className="text-sm">
                            Edit
                          </Label>
                        </div>
                      </RadioGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}